import cn from 'classnames';

import { useCheckoutContext, Article, ArticleBasedVoucher, Price } from '@zooplus/checkout-state';
import { isBitiba } from '@zooplus/commons-lang';
import { P1, P2, TextBadge } from '@zooplus/ui-components';

import ItemStandardPrice from '@components/cart/cart-table/cart-item/item-standard-price';
import ItemBasedDiscount from '@components/common/item-based-discount';
import PriceWrapper from '@components/common/price-wrapper/price-wrapper';
import { getAppConfig } from '@config/get-app-config';
import { useTranslation } from '@hooks/use-translations';
import SavingsPlan from '@public/images/savings-plan.svg';
import {
  CART_VERSION,
  ARTICLE_TYPE,
  PREVIEW_ITEM_IMAGE_DIMENSION,
  CHECKOUT_STATE_PROJECT_ID,
} from '@utilities/constants';
import { showFallbackImage } from '@utilities/fallback-image';
import { isObjectNotNullOrEmpty, trimLeadingHyphenAndSpaces } from '@utilities/utils';

import styles from './check-order-item.module.scss';

type CheckOrderItemProps = {
  article: Article;
};

const CheckOrderItem = ({ article }: CheckOrderItemProps) => {
  const { cartVersion, mainTogglesMap = {} } = useCheckoutContext(CHECKOUT_STATE_PROJECT_ID);
  const { t } = useTranslation();
  const { site } = getAppConfig();

  const {
    id,
    name,
    variantName,
    formattedSubtotal,
    grossBonusPoints,
    type,
    price,
    pictureUrl,
    quantity,
    actualPriceLabel,
    voucherApplied,
    lineItemDiscounts,
    articleBasedVoucher,
  } = article;

  const isBonusPointItem = type === ARTICLE_TYPE.BONUS;
  const isSavingsPlanItem = type === ARTICLE_TYPE.SAVINGS_PLAN;
  const isFreeSampleItem = type === ARTICLE_TYPE.FREE_SAMPLE;
  const isFreeItem = type === ARTICLE_TYPE.FREE;
  const isCartApiV2: boolean = cartVersion === CART_VERSION.V2;

  const abdApplied = isCartApiV2
    ? lineItemDiscounts?.articleBasedDiscount?.applied
    : voucherApplied;
  const abdDetails = isCartApiV2 ? lineItemDiscounts?.articleBasedDiscount : articleBasedVoucher;
  const { WIDTH, HEIGHT } = PREVIEW_ITEM_IMAGE_DIMENSION;
  const isNewPricingEnabled = mainTogglesMap.NEW_PRICING_COMPONENT_IN_CART_AND_PREVIEW;

  const renderBonusPointLabel = () => {
    return (
      <P1
        bold
        className={cn(styles.bonusPointLabel, isNewPricingEnabled && styles.newPriceBPLabel)}>
        {`${isCartApiV2 ? (price as Price)?.labelPrefix || '' : actualPriceLabel || ''}`}
      </P1>
    );
  };

  return (
    <article className={styles.itemMainContainer} data-testid="item-main-container" key={id}>
      <div className={styles.itemInfoWrapper}>
        {isSavingsPlanItem && !isBitiba(site.domain) ? (
          <SavingsPlan data-testid="savings-plan-image" />
        ) : (
          <img
            data-testid="item-image"
            loading="lazy"
            src={pictureUrl}
            alt={name}
            onError={(e) =>
              showFallbackImage(e, {
                width: WIDTH,
                height: HEIGHT,
              })
            }
            width={WIDTH}
            height={HEIGHT}
          />
        )}
        <div className={styles.itemNameWrapper}>
          <P1
            data-testid="item-name"
            bold
            className={cn(styles.itemName, styles.applyEllipsis)}
            title={name}>
            {name}
          </P1>
          {isFreeSampleItem ? (
            <TextBadge
              className={styles.itemTextBadge}
              label={t('cart.freeSample.title')}
              variant="positive"
            />
          ) : (
            !isSavingsPlanItem && (
              <P2
                data-testid="item-variant"
                className={cn(
                  styles.itemVariantName,
                  styles.applyEllipsis,
                  abdApplied && 'pp-mb-2xs',
                )}
                title={variantName}>
                {trimLeadingHyphenAndSpaces(variantName)}
              </P2>
            )
          )}
          {abdApplied && isObjectNotNullOrEmpty(abdDetails) && (
            <ItemBasedDiscount {...(abdDetails as ArticleBasedVoucher)} />
          )}
        </div>
      </div>
      <div
        className={cn(
          styles.itemQuantityPriceWrapper,
          !isSavingsPlanItem && !isFreeItem && styles.applyMarginTop,
        )}>
        {!isSavingsPlanItem && !isFreeItem && (
          <>
            <div data-testid="item-price" className={styles.itemPrice}>
              {isFreeSampleItem ? (
                <span className={styles.boldText}>{t('cart.freeSample.priceText')}</span>
              ) : (
                <>
                  <PriceWrapper article={article} />
                  {isBonusPointItem && renderBonusPointLabel()}
                </>
              )}
            </div>
            <P1 data-testid="item-quantity" className={styles.itemQuantity}>
              {`x${quantity}`}
            </P1>
          </>
        )}
        <div data-testid="item-total-price" className={styles.itemStandardPrice}>
          {isFreeSampleItem ? (
            <span className={styles.boldText}>{t('cart.freeSample.priceText')}</span>
          ) : (
            <>
              <ItemStandardPrice
                formattedSubtotal={formattedSubtotal}
                isBonusArticle={isBonusPointItem}
                points={grossBonusPoints}
                isNewPricingEnabled={isNewPricingEnabled}
                isPreviewPage
              />
              {isBonusPointItem && renderBonusPointLabel()}
            </>
          )}
        </div>
      </div>
    </article>
  );
};

export default CheckOrderItem;
